import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Tooltip } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

// DEMO: shared guided workflow stepper shown on the three Premium BDX screens.
// Purely navigational — it does not read or write any processing state.
const WORKFLOW_STEPS = [
  {
    label: "Upload BDX",
    caption: "Bring in the bordereau",
    route: "/BDX/PremiumBDX",
  },
  {
    label: "Review Exceptions",
    caption: "Investigate variances",
    route: "/BDX/ExceptionReport",
  },
  {
    label: "Release Payment",
    caption: "Approve matched payouts",
    route: "/BDX/PayoutSummary",
  },
];

const ACCENT = "#1672DE";

const DemoWorkflowNav = ({ activeStep = 0, onStepClick }) => {
  const navigate = useNavigate();

  const handleClick = (step, index) => {
    if (index === activeStep) return;
    if (onStepClick) {
      onStepClick(step, index);
      return;
    }
    navigate(step.route);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        gap: { xs: 0.5, sm: 1.5 },
        flexWrap: "nowrap",
        overflowX: "auto",
        backgroundColor: "#f8fafc",
        border: "1px solid #e8eaf0",
        borderRadius: "12px",
        padding: "10px 14px",
        marginBottom: "12px",
      }}
    >
      {WORKFLOW_STEPS.map((step, index) => {
        const isActive = index === activeStep;
        const isDone = index < activeStep;
        const circleBg = isActive ? ACCENT : isDone ? "#4caf50" : "#e2e8f0";
        const circleColor = isActive || isDone ? "#ffffff" : "#94a3b8";
        const labelColor = isActive ? ACCENT : isDone ? "#1f2937" : "#6b7280";

        return (
          <React.Fragment key={step.label}>
            <Tooltip title={isActive ? "You are here" : `Go to ${step.label}`}>
              <Box
                onClick={() => handleClick(step, index)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "6px 12px",
                  borderRadius: "10px",
                  cursor: isActive ? "default" : "pointer",
                  minWidth: "fit-content",
                  backgroundColor: isActive ? "rgba(22, 114, 222, 0.08)" : "transparent",
                  border: isActive ? `1px solid ${ACCENT}33` : "1px solid transparent",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: isActive ? "rgba(22, 114, 222, 0.08)" : "rgba(22, 114, 222, 0.05)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    backgroundColor: circleBg,
                    color: circleColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "13px",
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {isDone ? <CheckIcon sx={{ fontSize: 16 }} /> : index + 1}
                </Box>
                <Box sx={{ whiteSpace: "nowrap" }}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: labelColor, lineHeight: 1.2 }}>
                    {step.label}
                  </Typography>
                  <Typography sx={{ fontSize: "10.5px", color: "#9ca3af", lineHeight: 1.2 }}>
                    {step.caption}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
            {index < WORKFLOW_STEPS.length - 1 && (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 20,
                  alignSelf: "center",
                  height: 2,
                  backgroundColor: index < activeStep ? "#4caf50" : "#e2e8f0",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default DemoWorkflowNav;
